import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Empty, Popconfirm, Skeleton, Space, Tag, Typography } from 'antd';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { SortableList } from '@shared/components/SortableList';
import { useResponsiveModalWidth } from '@shared/hooks/useResponsiveModalWidth';
import { slidersApi } from '@api/sliders';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { SliderImageFormModal } from './SliderImageFormModal';
import type { SliderImage, SliderImageInput } from '../types';

const { Text } = Typography;

interface SliderImagesDrawerProps {
  open: boolean;
  sliderId: string | null;
  sliderName?: string;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function SliderImagesDrawer({
  open,
  sliderId,
  sliderName,
  canManage,
  onClose,
  onChanged,
}: SliderImagesDrawerProps) {
  const { t } = useTranslation(['widgets', 'common']);
  const qc = useQueryClient();
  const width = useResponsiveModalWidth(640);

  const [imageModal, setImageModal] = useState<{ open: boolean; editing: SliderImage | null }>({
    open: false,
    editing: null,
  });

  const { data: slider, isFetching } = useQuery({
    queryKey: ['slider', sliderId],
    queryFn: () => slidersApi.get(sliderId ?? ''),
    enabled: open && !!sliderId,
  });

  const serverImages = useMemo(() => slider?.images ?? [], [slider]);
  const baseKey = serverImages.map((i) => i.id).join(',');
  const [override, setOverride] = useState<{ key: string; ids: string[] } | null>(null);
  const images = useMemo(() => {
    const ids = override && override.key === baseKey ? override.ids : serverImages.map((i) => i.id);
    const map = new Map(serverImages.map((i) => [i.id, i]));
    return ids.map((id) => map.get(id)).filter((i): i is SliderImage => Boolean(i));
  }, [override, baseKey, serverImages]);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['slider', sliderId] });
    onChanged();
  };

  const reorderMutation = useMutation({
    mutationFn: (ordered: SliderImage[]) =>
      slidersApi.images.reorder(
        sliderId ?? '',
        ordered.map((img, idx) => ({ id: img.id, order: idx })),
      ),
    onSuccess: () => refresh(),
    onError: (err) => {
      setOverride(null);
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const saveImageMutation = useMutation({
    mutationFn: (vars: { input: SliderImageInput; editingId: string | null }) =>
      vars.editingId
        ? slidersApi.images.update(vars.editingId, vars.input)
        : slidersApi.images.create(sliderId ?? '', vars.input),
    onSuccess: async () => {
      toast.success(t('image.toast.saved'));
      setImageModal({ open: false, editing: null });
      await refresh();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imageId: string) => slidersApi.images.remove(imageId),
    onSuccess: async () => {
      toast.success(t('image.toast.deleted'));
      await refresh();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={width}
      title={t('image.drawerTitle', { name: sliderName ?? '' })}
      extra={
        canManage ? (
          <Button
            type="primary"
            icon={<Plus size={15} />}
            onClick={() => setImageModal({ open: true, editing: null })}
          >
            {t('image.new')}
          </Button>
        ) : null
      }
    >
      {isFetching && !slider ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : images.length === 0 ? (
        <Empty description={t('image.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <SortableList<SliderImage>
          items={images}
          getId={(i) => i.id}
          disabled={!canManage}
          onReorder={(next) => {
            setOverride({ key: baseKey, ids: next.map((i) => i.id) });
            reorderMutation.mutate(next);
          }}
          renderItem={(img, idx) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minWidth: 0 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: 'var(--ds-text-secondary)', marginRight: 6, fontVariantNumeric: 'tabular-nums' }}>
                    #{idx + 1}
                  </span>
                  {img.title || img.imageUrl}
                </div>
                <Space size={6} style={{ marginTop: 4 }} wrap>
                  {!img.isActive ? <Tag color="default">{t('image.inactive')}</Tag> : null}
                  {img.startDate || img.endDate ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {img.startDate ? dayjs(img.startDate).format('DD.MM.YYYY') : '…'} —{' '}
                      {img.endDate ? dayjs(img.endDate).format('DD.MM.YYYY') : '…'}
                    </Text>
                  ) : null}
                </Space>
              </div>
              {canManage ? (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button size="small" icon={<Pencil size={13} />} onClick={() => setImageModal({ open: true, editing: img })} />
                  <Popconfirm
                    title={t('image.delete.title')}
                    okText={t('common:actions.delete')}
                    okButtonProps={{ danger: true }}
                    cancelText={t('common:actions.cancel')}
                    onConfirm={() => deleteImageMutation.mutate(img.id)}
                  >
                    <Button size="small" danger icon={<Trash2 size={13} />} />
                  </Popconfirm>
                </Space>
              ) : null}
            </div>
          )}
        />
      )}

      <SliderImageFormModal
        open={imageModal.open}
        editing={imageModal.editing}
        saving={saveImageMutation.isPending}
        onCancel={() => setImageModal({ open: false, editing: null })}
        onSubmit={(input, editingId) => saveImageMutation.mutate({ input, editingId })}
      />
    </Drawer>
  );
}
